import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaChalkboardTeacher, FaClock, FaGraduationCap, FaLock, FaPlus, FaUsers } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useTranslation } from '../contexts/TranslationContext.tsx';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus.ts';
import { useSeoMeta } from '../hooks/useSeoMeta.ts';
import { API_BASE } from '../utils/apiEndpoints';
import './css/EducationPage.css';

type CurrentAssignment = {
  lesson_id: number;
  title: string;
  due_at?: string | null;
  incomplete?: boolean;
};

type StudyClass = {
  id: number;
  name: string;
  studentsCount: number;
  lessonsCount?: number;
  unreadSubmissions?: number;
  progress?: { solved: number; total: number };
  current_assignment?: CurrentAssignment | null;
};

const MAX_TEACHER_CLASSES = 3;

function localTodayYmd() {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
}

function EducationPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  const { hasActiveSubscription } = useSubscriptionStatus();
  const canTeach = user?.role === 'admin' || user?.role === 'teacher';

  const [joined, setJoined] = useState<StudyClass[]>([]);
  const [created, setCreated] = useState<StudyClass[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newClassName, setNewClassName] = useState('');
  const [creating, setCreating] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);

  useSeoMeta({
    title: `${t('education.title')} — Sciplex`,
    description: t('education.pageDesc'),
    canonicalPath: '/my-class',
    noIndex: true,
  });

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/classes/my-study?today=${encodeURIComponent(localTodayYmd())}`,
          { credentials: 'include' },
        );
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok) {
          setError(data.message || t('education.loadError'));
          setJoined([]);
          setCreated([]);
          return;
        }
        setJoined(Array.isArray(data.joined) ? data.joined : []);
        setCreated(Array.isArray(data.created) ? data.created : []);
      } catch {
        if (!cancelled) {
          setError(t('education.loadError'));
          setJoined([]);
          setCreated([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, t]);

  const handleCreateClass = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canTeach) return;
    if (!hasActiveSubscription) {
      navigate('/subscription');
      return;
    }
    const name = newClassName.trim();
    if (!name) return;
    setCreating(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/classes`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || t('education.createError'));
      navigate(`/class/${data.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('education.createError'));
    } finally {
      setCreating(false);
    }
  };

  const canCreateMore = canTeach && created.length < MAX_TEACHER_CLASSES;
  const showTeacherColumn = canTeach || created.length > 0;
  const today = localTodayYmd();

  const assignmentDueLabel = (assignment?: CurrentAssignment | null) => {
    if (!assignment?.due_at) return null;
    if (assignment.due_at < today) return t('education.overdue');
    return t('education.dueBy').replace('{date}', assignment.due_at);
  };

  const studentHref = (item: StudyClass) => (
    item.current_assignment?.incomplete && item.current_assignment.lesson_id
      ? `/class/${item.id}?lesson=${item.current_assignment.lesson_id}`
      : `/class/${item.id}`
  );

  const teacherHref = (item: StudyClass) => {
    if (item.unreadSubmissions) return `/class/${item.id}?tab=analytics`;
    if (item.current_assignment) return `/class/${item.id}?tab=assignment`;
    return `/class/${item.id}`;
  };

  if (authLoading || !isAuthenticated) {
    return (
      <div className="education-page">
        <div className="education-status">{t('education.loading')}</div>
      </div>
    );
  }

  return (
    <div className="education-page">
      <div className="education-wrap">
        <header className="education-header">
          <h1 className="education-title">{t('education.title')}</h1>
          <p className="education-lead">{t('education.lead')}</p>
        </header>

        {error && <div className="education-error">{error}</div>}

        {loading ? (
          <div className="education-status">{t('education.loading')}</div>
        ) : (
          <div className={`education-columns${showTeacherColumn ? '' : ' education-columns--single'}`}>
            <section className="education-column" aria-labelledby="education-student-heading">
              <div className="education-column-head">
                <span className="education-column-icon education-column-icon--student" aria-hidden>
                  <FaGraduationCap />
                </span>
                <div>
                  <h2 id="education-student-heading">{t('education.studentColumn')}</h2>
                  <p>{t('education.studentColumnHint')}</p>
                </div>
              </div>

              {joined.length === 0 ? (
                <div className="education-empty">
                  <p>{t('education.studentEmpty')}</p>
                  <Link to="/join-class" className="education-btn education-btn--primary">
                    {t('education.studentEmptyCta')}
                  </Link>
                </div>
              ) : (
                <ul className="education-list">
                  {joined.map((item) => {
                    const assignment = item.current_assignment?.incomplete ? item.current_assignment : null;
                    const overdue = Boolean(assignment?.due_at && assignment.due_at < today);
                    const progress = item.progress;
                    const pct = progress && progress.total > 0
                      ? Math.min(100, Math.round((progress.solved / progress.total) * 100))
                      : 0;
                    const dueLabel = assignmentDueLabel(assignment);
                    return (
                      <li key={item.id}>
                        <Link
                          to={studentHref(item)}
                          className={`education-card${overdue ? ' education-card--overdue' : ''}`}
                        >
                          <div className="education-card-name">{item.name}</div>
                          {progress && progress.total > 0 ? (
                            <>
                              <div
                                className="education-card-progress"
                                role="progressbar"
                                aria-valuemin={0}
                                aria-valuemax={progress.total}
                                aria-valuenow={progress.solved}
                              >
                                <span className="education-card-progress-fill" style={{ width: `${pct}%` }} />
                              </div>
                              <div className="education-card-meta">
                                <span>
                                  {t('education.progressOf')
                                    .replace('{solved}', String(progress.solved))
                                    .replace('{total}', String(progress.total))}
                                </span>
                              </div>
                            </>
                          ) : (
                            <div className="education-card-meta">
                              <span>{t('education.lessonsCount').replace('{count}', String(item.lessonsCount || 0))}</span>
                            </div>
                          )}
                          {assignment ? (
                            <div className="education-card-work">
                              <FaClock aria-hidden />
                              <div>
                                <div className="education-card-work-title">{assignment.title}</div>
                                {dueLabel ? (
                                  <div className={`education-card-due${overdue ? ' is-overdue' : ''}`}>{dueLabel}</div>
                                ) : null}
                              </div>
                            </div>
                          ) : null}
                          <span className="education-card-cta">
                            {assignment ? t('education.continueWork') : t('education.openClass')}
                          </span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

            {showTeacherColumn && (
            <section className="education-column" aria-labelledby="education-teacher-heading">
              <div className="education-column-head">
                <span className="education-column-icon education-column-icon--teacher" aria-hidden>
                  <FaChalkboardTeacher />
                </span>
                <div>
                  <h2 id="education-teacher-heading">{t('education.teacherColumn')}</h2>
                  <p>{t('education.teacherColumnHint')}</p>
                </div>
              </div>

              {created.length === 0 ? (
                <div className="education-empty">
                  <p>{canTeach ? t('education.teacherEmpty') : t('education.teacherEmptyStudent')}</p>
                </div>
              ) : (
                <ul className="education-list">
                  {created.map((item) => {
                    const assignment = item.current_assignment;
                    const dueLabel = assignmentDueLabel(assignment);
                    const overdue = Boolean(assignment?.due_at && assignment.due_at < today);
                    const cta = item.unreadSubmissions
                      ? t('education.reviewSubmissions')
                      : assignment
                        ? t('education.openAssignment')
                        : t('education.openClass');
                    return (
                      <li key={item.id}>
                        <Link
                          to={teacherHref(item)}
                          className={`education-card${item.unreadSubmissions ? ' education-card--unread' : ''}`}
                        >
                          <div className="education-card-name">
                            {item.name}
                            {item.unreadSubmissions ? (
                              <span className="education-card-badge">
                                {t('education.newSubmissions').replace(
                                  '{count}',
                                  String(item.unreadSubmissions),
                                )}
                              </span>
                            ) : null}
                          </div>
                          <div className="education-card-meta">
                            <span>
                              <FaUsers aria-hidden />
                              {t('education.studentsCount').replace('{count}', String(item.studentsCount))}
                            </span>
                            {item.lessonsCount != null ? (
                              <span>{t('education.lessonsCount').replace('{count}', String(item.lessonsCount))}</span>
                            ) : null}
                          </div>
                          {assignment ? (
                            <div className="education-card-work">
                              <FaClock aria-hidden />
                              <div>
                                <div className="education-card-work-title">{assignment.title}</div>
                                {dueLabel ? (
                                  <div className={`education-card-due${overdue ? ' is-overdue' : ''}`}>{dueLabel}</div>
                                ) : null}
                              </div>
                            </div>
                          ) : null}
                          <span className="education-card-cta">{cta}</span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}

              {canTeach && canCreateMore && (
                <div className="education-create">
                  {!showCreateForm ? (
                    <button
                      type="button"
                      className="education-btn education-btn--primary"
                      onClick={() => {
                        if (!hasActiveSubscription) {
                          navigate('/subscription');
                          return;
                        }
                        setShowCreateForm(true);
                      }}
                    >
                      {hasActiveSubscription ? <FaPlus aria-hidden /> : <FaLock aria-hidden />}
                      {t('education.createClass')}
                    </button>
                  ) : (
                    <form onSubmit={handleCreateClass} className="education-create-form">
                      <input
                        type="text"
                        className="education-input"
                        value={newClassName}
                        onChange={(e) => setNewClassName(e.target.value)}
                        placeholder={t('education.classNamePlaceholder')}
                        maxLength={200}
                        autoFocus
                      />
                      <div className="education-create-actions">
                        <button
                          type="button"
                          className="education-btn"
                          onClick={() => {
                            setShowCreateForm(false);
                            setNewClassName('');
                          }}
                        >
                          {t('education.cancel')}
                        </button>
                        <button
                          type="submit"
                          className="education-btn education-btn--primary"
                          disabled={creating || !newClassName.trim()}
                        >
                          {creating ? t('education.creating') : t('education.createClass')}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              )}
            </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default EducationPage;
